import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { navLinks, companyInfo } from '../data/content';
import { Button } from './ui/button';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="section-container">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
            <img 
              src="/logo-transparent.png" 
              alt="Apollo House Student Village" 
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.id}
                to={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`nav-link font-medium ${
                  location.pathname === link.href ? 'active text-orange-500' : ''
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href={`tel:${companyInfo.phone.tel}`}
              className="flex items-center gap-2 text-slate-700 hover:text-coral transition-colors"
              data-testid="navbar-phone"
            >
              <Phone className="w-4 h-4" />
              <span className="font-medium">{companyInfo.phone.tel}</span>
            </a>
            <Link to="/contact">
              <Button
                data-testid="navbar-apply-btn"
                className="bg-gradient-to-r from-coral to-orange hover:from-coral-dark hover:to-coral text-white rounded-full px-6 font-semibold shadow-lg shadow-coral/25"
              >
                Apply Now
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-slate-700 hover:text-coral transition-colors"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden absolute top-20 left-0 right-0 bg-white shadow-lg border-t border-orange-100"
          >
            <div className="py-4 px-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.id}
                  to={link.href}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`block py-3 px-4 rounded-xl font-medium transition-colors ${
                    location.pathname === link.href
                      ? 'bg-coral/10 text-coral'
                      : 'text-slate-700 hover:bg-orange-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 border-t border-orange-100 mt-4 space-y-3">
                <a
                  href={`tel:${companyInfo.phone.tel}`}
                  className="flex items-center gap-2 text-slate-700 py-2 px-4"
                >
                  <Phone className="w-4 h-4" />
                  <span>{companyInfo.phone.tel}</span>
                </a>
                <Link to="/contact" className="block">
                  <Button
                    data-testid="mobile-apply-btn"
                    className="w-full bg-gradient-to-r from-coral to-orange hover:from-coral-dark hover:to-coral text-white rounded-full font-semibold"
                  >
                    Apply Now
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
